import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { toast } from "sonner";

type AuthSearch = {
  redirect?: string;
};

export const Route = createFileRoute("/auth")({
  ssr: false,
  validateSearch: (search: Record<string, unknown>): AuthSearch => ({
    redirect:
      typeof search.redirect === "string" && search.redirect.startsWith("/")
        ? search.redirect
        : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Sign in — Glow Long Golf" },
      {
        name: "description",
        content:
          "Sign in or create an account to track your Glow Long Golf orders, manage your address, and reorder your favorite glow packs.",
      },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const nav = useNavigate();
  const { redirect } = Route.useSearch();
  const [mode, setMode] = useState<"signin" | "signup" | "forgot">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const postAuthPath = redirect ?? "/account";

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data, error }) => {
      if (data.user) {
        nav({ to: postAuthPath });
        return;
      }
      if (error) {
        await supabase.auth.signOut({ scope: "local" });
      }
    });
  }, [nav, postAuthPath]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        toast.success("Welcome back");
        nav({ to: postAuthPath });
      } else if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}${postAuthPath}` },
        });
        if (error) throw error;
        toast.success("Account created — check your email to confirm.");
      } else {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        toast.success("Password reset link sent — check your email.");
        setMode("signin");
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  async function google() {
    const res = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}${postAuthPath}`,
    });
    if ((res as any)?.error) toast.error("Sign in failed — please try again.");
  }

  const titles: Record<typeof mode, string> = {
    signin: "Sign in",
    signup: "Create your account",
    forgot: "Reset your password",
  };

  return (
    <section className="mx-auto max-w-md px-4 py-20 sm:px-6">
      <div className="rounded-3xl border border-border bg-card p-8 shadow-glow">
        <h1 className="font-display text-2xl font-bold">{titles[mode]}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {mode === "forgot"
            ? "Enter your email and we'll send you a reset link."
            : "Track orders, manage events, or — if this is your first time — claim admin."}
        </p>

        {mode !== "forgot" && (
          <>
            <button
              onClick={google}
              className="mt-6 w-full rounded-full border border-border bg-background px-4 py-3 text-sm font-medium hover:bg-secondary"
            >
              Continue with Google
            </button>
            <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
              <div className="h-px flex-1 bg-border" /> or email{" "}
              <div className="h-px flex-1 bg-border" />
            </div>
          </>
        )}

        <form onSubmit={submit} className="space-y-3">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="h-11 w-full rounded-lg border border-input bg-background px-4 text-sm"
          />
          {mode !== "forgot" && (
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password (8+ characters)"
              className="h-11 w-full rounded-lg border border-input bg-background px-4 text-sm"
            />
          )}
          <button
            type="submit"
            disabled={loading}
            className="h-11 w-full rounded-full bg-primary text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90 disabled:opacity-60"
          >
            {loading
              ? "…"
              : mode === "signin"
                ? "Sign in"
                : mode === "signup"
                  ? "Create account"
                  : "Send reset link"}
          </button>
        </form>

        <div className="mt-4 flex flex-col gap-2 text-xs text-muted-foreground">
          {mode === "signin" && (
            <>
              <button onClick={() => setMode("forgot")} className="hover:text-foreground">
                Forgot your password?
              </button>
              <button onClick={() => setMode("signup")} className="hover:text-foreground">
                Don't have an account? Sign up
              </button>
            </>
          )}
          {mode === "signup" && (
            <button onClick={() => setMode("signin")} className="hover:text-foreground">
              Already have an account? Sign in
            </button>
          )}
          {mode === "forgot" && (
            <button onClick={() => setMode("signin")} className="hover:text-foreground">
              Back to sign in
            </button>
          )}
        </div>
      </div>
      <p className="mt-4 text-center text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          ← Back to home
        </Link>
      </p>
    </section>
  );
}
